import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  collection, collectionGroup, doc, getDocs, onSnapshot, query, where, updateDoc, runTransaction, serverTimestamp,
} from "firebase/firestore";
import { db, auth } from "../firebase";
import type { AppDispatch } from "./store";

/* ───────────────── SCHEMA (all Firestore) ─────────────────

     orders/{customerId}                          root doc, holds customerId
     orders/{customerId}/orders/{orderId}         paid order (written by the backend only)
     orders/{customerId}/PendingOrders/{orderId}  awaiting payment (written by the backend only)

   customerId == the buyer's uid
   ownerIds[] == uid of every seller in the order (items[].ownerId), written by the backend

   The backend creates orders after payment (handleWebhook / mpesaPay). The client only
   changes status, delivery and cancel/refund bookkeeping, and the Firestore rules limit
   exactly which fields each side may touch.
──────────────────────────────────────────────────────────── */

const DB_COLLECTION = "orders";
const DB_SUBCOLLECTION = "orders";
const DB_PENDING_SUBCOLLECTION = "PendingOrders";

const ordersCol = (customerId: string) => collection(db, DB_COLLECTION, customerId, DB_SUBCOLLECTION);
const orderRef = (customerId: string, orderId: string) => doc(db, DB_COLLECTION, customerId, DB_SUBCOLLECTION, orderId);
const pendingCol = (customerId: string) => collection(db, DB_COLLECTION, customerId, DB_PENDING_SUBCOLLECTION);
const ordersGroup = () => collectionGroup(db, DB_SUBCOLLECTION);

/* ───────────────── TYPES ───────────────── */

export type OrderStatus = "pending" | "paid" | "shipped" | "delivered" | "cancelled" | "refunded";

export type DeliveryStatus = { sellerDelivered: boolean; buyerReceived: boolean };

export interface OrderItem {
  productId: string;
  ownerId?: string;
  name: string;
  price: number;
  quantity: number;
  photo?: string;
}

export interface Order {
  id: string;
  orderId: string;
  customerId: string;
  items: OrderItem[];
  ownerIds: string[];
  total: number; // EUR
  totalQty: number;
  status: OrderStatus;

  isPaid: boolean;
  isCancelled: boolean;
  isRefunded: boolean;
  isDelivered: boolean;
  deliveryStatus: DeliveryStatus;

  paymentId?: string;
  paymentProvider?: string;
  currency?: string;
  cancelReason?: string;
  refundReason?: string;
  refundStatus?: string;

  createdAt?: number | null;
  updatedAt?: number | null;
  [key: string]: any;
}

export interface PendingOrder {
  orderId: string;
  customerId: string;
  status: string;
  total?: number;
  createdAt?: number | null;
  [key: string]: any;
}

type OrdersState = {
  orders: Order[];            // the signed-in customer's orders
  hostOrders: Order[];        // orders that contain this owner's products
  currentOrder: Order | null;
  pendingOrders: PendingOrder[];
  hostCount: number;
  hostDelivered: number;
  hostRevenue: number;
  status: "idle" | "loading" | "failed";
  loading: boolean;
  actionLoading: boolean;
  error: string | null;
};

const initialState: OrdersState = {
  orders: [], hostOrders: [], currentOrder: null, pendingOrders: [],
  hostCount: 0, hostDelivered: 0, hostRevenue: 0,
  status: "idle", loading: false, actionLoading: false, error: null,
};

/* ───────────────── HELPERS ───────────────── */

const toMillis = (v: any): number | null => v?.toMillis?.() ?? null;
const EMPTY_STATUS: DeliveryStatus = { sellerDelivered: false, buyerReceived: false };

const parseOrder = (id: string, raw: Record<string, any>, fallbackCustomerId = ""): Order =>
  ({
    id,
    orderId: raw.orderId ?? id,
    customerId: raw.customerId ?? fallbackCustomerId,
    items: raw.items ?? [],
    ownerIds: raw.ownerIds ?? [],
    total: raw.total ?? 0,
    totalQty: raw.totalQty ?? 0,
    status: raw.status ?? "paid",
    isPaid: raw.isPaid ?? raw.status === "paid",
    isCancelled: raw.isCancelled ?? false,
    isRefunded: raw.isRefunded ?? false,
    isDelivered: raw.isDelivered ?? false,
    deliveryStatus: raw.deliveryStatus ?? EMPTY_STATUS,
    ...raw,
    createdAt: toMillis(raw.createdAt),
    updatedAt: toMillis(raw.updatedAt),
  } as Order);

// path: orders/{customerId}/orders/{orderId}
const parseOrderDoc = (d: { id: string; data: () => any; ref: any }) =>
  parseOrder(d.id, d.data() || {}, d.ref?.parent?.parent?.id ?? "");

const sortNewest = (list: Order[]) => [...list].sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));

/* the collection group "orders" also returns the root docs orders/{customerId}; real orders carry items */
const isOrder = (o: Order) => Array.isArray(o.items) && o.items.length > 0;

/* ───────────────── LISTENERS ─────────────────
   Unsubscribe functions live here (not in Redux state), and every listener
   returns its own unsubscribe so a component can use it as an effect cleanup. */

let listUnsub: (() => void) | null = null;
let currentUnsub: (() => void) | null = null;

const stopList = () => { if (listUnsub) { listUnsub(); listUnsub = null; } };
const stopCurrent = () => { if (currentUnsub) { currentUnsub(); currentUnsub = null; } };

/* role "customer" -> direct path orders/{userId}/orders   (no index)
   role "owner"    -> collectionGroup("orders") where ownerIds array-contains userId
                      (needs a collection-group index on `ownerIds`; the console prints the link on first run) */
export const listenToOrders =
  (userId?: string, role: "customer" | "owner" = "customer") =>
  (dispatch: AppDispatch): (() => void) => {
    const uid = userId || auth.currentUser?.uid;
    if (!uid) return () => {};

    stopList();
    dispatch(setLoading(true));

    const handleErr = (err: Error) => { dispatch(setOrdersError(err.message)); dispatch(setLoading(false)); };

    if (role === "owner") {
      listUnsub = onSnapshot(
        query(ordersGroup(), where("ownerIds", "array-contains", uid)),
        (snap) => { dispatch(setHostOrders(sortNewest(snap.docs.map(parseOrderDoc).filter(isOrder)))); dispatch(setLoading(false)); },
        handleErr
      );
    } else {
      listUnsub = onSnapshot(
        ordersCol(uid),
        (snap) => { dispatch(setOrders(sortNewest(snap.docs.map(parseOrderDoc)))); dispatch(setLoading(false)); },
        handleErr
      );
    }
    return stopList;
  };

/* ONE order. customerId known -> direct doc read. Unknown -> collectionGroup where orderId == orderId. */
export const listenToOrderById =
  (orderId: string, customerId?: string) =>
  (dispatch: AppDispatch): (() => void) => {
    if (!orderId) return () => {};
    stopCurrent();

    if (customerId) {
      currentUnsub = onSnapshot(
        orderRef(customerId, orderId),
        (snap) => dispatch(setCurrentOrder(snap.exists() ? parseOrder(snap.id, snap.data() || {}, customerId) : null)),
        (err) => dispatch(setOrdersError(err.message))
      );
    } else {
      currentUnsub = onSnapshot(
        query(ordersGroup(), where("orderId", "==", orderId)),
        (snap) => { const d = snap.docs[0]; dispatch(setCurrentOrder(d ? parseOrderDoc(d) : null)); },
        (err) => dispatch(setOrdersError(err.message))
      );
    }
    return stopCurrent;
  };

export const stopOrdersListeners = () => (dispatch: AppDispatch) => {
  stopList();
  stopCurrent();
  dispatch(clearOrders());
};

/* kept for the existing pages */
export const listenToUserOrders = (customerId?: string) => listenToOrders(customerId, "customer");
export const listenToHostOrders = (ownerId?: string) => listenToOrders(ownerId, "owner");

/* ───────────────── CANCEL ─────────────────
   Customer: only while the order is still "paid" (not shipped). Owner of the order: same.
   Updates Firestore only. It does not refund money or put stock back. */
export const cancelOrder = createAsyncThunk<
  { customerId: string; orderId: string },
  { customerId: string; orderId: string; reason?: string },
  { rejectValue: string }
>("orders/cancel", async ({ customerId, orderId, reason }, { rejectWithValue }) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated");
    const ref = orderRef(customerId, orderId);

    await runTransaction(db, async (t) => {
      const snap = await t.get(ref);
      if (!snap.exists()) throw new Error("Order not found");
      const order = snap.data();

      const isBuyer = user.uid === customerId;
      const isSeller = (order.ownerIds || []).includes(user.uid);
      if (!isBuyer && !isSeller) throw new Error("Not allowed to cancel this order");

      if (order.isCancelled) throw new Error("Order already cancelled");
      if (order.isRefunded) throw new Error("Order already refunded");
      if (order.isDelivered) throw new Error("Delivered orders can't be cancelled");
      if (order.deliveryStatus?.sellerDelivered === true || order.status === "shipped") throw new Error("Order already shipped, can't cancel");

      t.update(ref, {
        status: "cancelled",
        isCancelled: true,
        cancelledAt: serverTimestamp(),
        cancelledBy: user.uid,
        cancelReason: reason || "",
        updatedAt: serverTimestamp(),
      });
    });
    return { customerId, orderId };
  } catch (err: any) {
    return rejectWithValue(err.message || "Failed to cancel order");
  }
});

/* ───────────────── REFUND ─────────────────
   Sellers only. Records the refund in Firestore. It does not move money:
   refund in the Stripe / M-Pesa / Moolables dashboard. */
export const refundOrder = createAsyncThunk<
  { customerId: string; orderId: string },
  { customerId: string; orderId: string; reason?: string },
  { rejectValue: string }
>("orders/refund", async ({ customerId, orderId, reason }, { rejectWithValue }) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated");
    const ref = orderRef(customerId, orderId);

    await runTransaction(db, async (t) => {
      const snap = await t.get(ref);
      if (!snap.exists()) throw new Error("Order not found");
      const order = snap.data();

      if (!(order.ownerIds || []).includes(user.uid)) throw new Error("Only the seller can refund this order");
      if (!order.isPaid) throw new Error("Order was never paid");
      if (order.isRefunded) throw new Error("Order already refunded");

      t.update(ref, {
        status: "refunded",
        isRefunded: true,
        isCancelled: true,
        refundStatus: "refunded",
        refundReason: reason || "",
        refundedAt: serverTimestamp(),
        refundedBy: user.uid,
        updatedAt: serverTimestamp(),
      });
    });
    return { customerId, orderId };
  } catch (err: any) {
    return rejectWithValue(err.message || "Failed to refund order");
  }
});

/* ───────────────── MARK DELIVERED ─────────────────
   role "owner"    -> sellerDelivered = true, status "shipped"
   role "customer" -> buyerReceived = true, status "delivered" (only after it shipped) */
export const markOrderDelivered = createAsyncThunk<
  { customerId: string; orderId: string; status: OrderStatus; isDelivered: boolean; deliveryStatus: DeliveryStatus },
  { customerId: string; orderId: string; role?: "owner" | "customer" },
  { rejectValue: string }
>("orders/markDelivered", async ({ customerId, orderId, role = "owner" }, { rejectWithValue }) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated");
    const ref = orderRef(customerId, orderId);
    let out: { status: OrderStatus; isDelivered: boolean; deliveryStatus: DeliveryStatus } | null = null;

    await runTransaction(db, async (t) => {
      const snap = await t.get(ref);
      if (!snap.exists()) throw new Error("Order not found");
      const order = snap.data();
      if (order.isCancelled || order.isRefunded) throw new Error("Order is cancelled or refunded");

      const current: DeliveryStatus = { ...EMPTY_STATUS, ...(order.deliveryStatus || {}) };

      if (role === "customer") {
        if (user.uid !== customerId) throw new Error("You are not the buyer of this order");
        if (!current.sellerDelivered) throw new Error("The seller hasn't shipped this order yet");
        const deliveryStatus = { ...current, buyerReceived: true };
        out = { status: "delivered", isDelivered: true, deliveryStatus };
      } else {
        if (!(order.ownerIds || []).includes(user.uid)) throw new Error("You are not a seller on this order");
        const deliveryStatus = { ...current, sellerDelivered: true };
        out = { status: current.buyerReceived ? "delivered" : "shipped", isDelivered: current.buyerReceived, deliveryStatus };
      }
      t.update(ref, { ...out, updatedAt: serverTimestamp() });
    });

    const done = out as unknown as { status: OrderStatus; isDelivered: boolean; deliveryStatus: DeliveryStatus };
    return { customerId, orderId, ...done };
  } catch (err: any) {
    return rejectWithValue(err.message || "Failed to update delivery");
  }
});

/** Owner sets a status from a dropdown. Keeps the flags in step with it. */
export const upsertOrder = createAsyncThunk<
  { customerId: string; orderId: string; status: OrderStatus },
  { customerId: string; orderId: string; status: OrderStatus },
  { rejectValue: string }
>("orders/upsertOrder", async (p, { rejectWithValue }) => {
  try {
    const patch: Record<string, any> = { status: p.status, updatedAt: serverTimestamp() };
    if (p.status === "cancelled") patch.isCancelled = true;
    if (p.status === "refunded") { patch.isRefunded = true; patch.isCancelled = true; patch.refundStatus = "refunded"; }
    if (p.status === "shipped") patch["deliveryStatus.sellerDelivered"] = true;
    if (p.status === "delivered") { patch.isDelivered = true; patch["deliveryStatus.sellerDelivered"] = true; patch["deliveryStatus.buyerReceived"] = true; }
    await updateDoc(orderRef(p.customerId, p.orderId), patch);
    return p;
  } catch (err: any) { return rejectWithValue(err.message); }
});

/* ───────────────── ONE-TIME READS ───────────────── */

export const fetchUserOrders = createAsyncThunk<Order[], void, { rejectValue: string }>(
  "orders/fetchUserOrders",
  async (_, { rejectWithValue }) => {
    try {
      const u = auth.currentUser;
      if (!u) throw new Error("Not logged in");
      const snap = await getDocs(ordersCol(u.uid));
      return sortNewest(snap.docs.map(parseOrderDoc));
    } catch (err: any) { return rejectWithValue(err.message); }
  }
);

/** Orders that contain the signed-in owner's products. */
export const fetchHostOrders = createAsyncThunk<Order[], void, { rejectValue: string }>(
  "orders/fetchHostOrders",
  async (_, { rejectWithValue }) => {
    try {
      const u = auth.currentUser;
      if (!u) throw new Error("Not logged in");
      const snap = await getDocs(query(ordersGroup(), where("ownerIds", "array-contains", u.uid)));
      return sortNewest(snap.docs.map(parseOrderDoc).filter(isOrder));
    } catch (err: any) { return rejectWithValue(err.message); }
  }
);

export const fetchHostOrderStats = createAsyncThunk<
  { total: number; delivered: number; revenue: number },
  void,
  { rejectValue: string }
>("orders/fetchHostOrderStats", async (_, { rejectWithValue }) => {
  try {
    const u = auth.currentUser;
    if (!u) throw new Error("Not logged in");
    const snap = await getDocs(query(ordersGroup(), where("ownerIds", "array-contains", u.uid)));
    let total = 0, delivered = 0, revenue = 0;
    snap.docs.map(parseOrderDoc).filter(isOrder).forEach((o) => {
      if (o.isCancelled || o.isRefunded || !o.isPaid) return;
      total++;
      /* only this owner's lines */
      revenue += o.items.filter((i) => i.ownerId === u.uid).reduce((s, i) => s + i.price * i.quantity, 0);
      if (o.isDelivered || o.status === "delivered") delivered++;
    });
    return { total, delivered, revenue: +revenue.toFixed(2) };
  } catch (err: any) { return rejectWithValue(err.message); }
});

export const fetchPendingOrders = createAsyncThunk<PendingOrder[], string | undefined, { rejectValue: string }>(
  "orders/fetchPending",
  async (customerId, { rejectWithValue }) => {
    try {
      const uid = customerId || auth.currentUser?.uid;
      if (!uid) throw new Error("Not logged in");
      const snap = await getDocs(pendingCol(uid));
      return snap.docs.map((d) => {
        const raw = d.data() || {};
        return { ...raw, orderId: raw.orderId ?? d.id, customerId: raw.customerId ?? uid, status: raw.status ?? "awaiting-payment", createdAt: toMillis(raw.createdAt) } as PendingOrder;
      });
    } catch (err: any) { return rejectWithValue(err.message || "Failed to fetch pending orders"); }
  }
);

/* ───────────────── SLICE ───────────────── */

const patchOrder = (s: OrdersState, orderId: string, patch: Partial<Order>) => {
  const apply = (list: Order[]) => {
    const i = list.findIndex((o) => o.orderId === orderId);
    if (i !== -1) list[i] = { ...list[i], ...patch };
  };
  apply(s.orders);
  apply(s.hostOrders);
  if (s.currentOrder?.orderId === orderId) s.currentOrder = { ...s.currentOrder, ...patch };
};

const ordersSlice = createSlice({
  name: "orders",
  initialState,
  reducers: {
    setOrders: (s, a: PayloadAction<Order[]>) => { s.orders = a.payload; s.status = "idle"; s.error = null; },
    setHostOrders: (s, a: PayloadAction<Order[]>) => { s.hostOrders = a.payload; s.status = "idle"; s.error = null; },
    setCurrentOrder: (s, a: PayloadAction<Order | null>) => { s.currentOrder = a.payload; },
    setLoading: (s, a: PayloadAction<boolean>) => { s.loading = a.payload; if (a.payload) s.status = "loading"; },
    setOrdersError: (s, a: PayloadAction<string | null>) => { s.error = a.payload; s.status = a.payload ? "failed" : "idle"; },
    clearOrders: (s) => { s.orders = []; s.hostOrders = []; s.currentOrder = null; s.pendingOrders = []; s.error = null; },
  },
  extraReducers: (b) => {
    b.addCase(cancelOrder.pending, (s) => { s.actionLoading = true; s.error = null; })
      .addCase(cancelOrder.fulfilled, (s, a) => { s.actionLoading = false; patchOrder(s, a.payload.orderId, { status: "cancelled", isCancelled: true }); })
      .addCase(cancelOrder.rejected, (s, a) => { s.actionLoading = false; s.error = a.payload || "Failed to cancel order"; })

      .addCase(refundOrder.pending, (s) => { s.actionLoading = true; s.error = null; })
      .addCase(refundOrder.fulfilled, (s, a) => { s.actionLoading = false; patchOrder(s, a.payload.orderId, { status: "refunded", isRefunded: true, isCancelled: true, refundStatus: "refunded" }); })
      .addCase(refundOrder.rejected, (s, a) => { s.actionLoading = false; s.error = a.payload || "Failed to refund order"; })

      .addCase(markOrderDelivered.pending, (s) => { s.actionLoading = true; s.error = null; })
      .addCase(markOrderDelivered.fulfilled, (s, a) => {
        s.actionLoading = false;
        patchOrder(s, a.payload.orderId, { status: a.payload.status, isDelivered: a.payload.isDelivered, deliveryStatus: a.payload.deliveryStatus });
      })
      .addCase(markOrderDelivered.rejected, (s, a) => { s.actionLoading = false; s.error = a.payload || "Failed to update delivery"; })

      .addCase(upsertOrder.fulfilled, (s, a) => { patchOrder(s, a.payload.orderId, { status: a.payload.status }); })
      .addCase(upsertOrder.rejected, (s, a) => { s.error = a.payload || "Couldn't update the order"; })

      .addCase(fetchUserOrders.pending, (s) => { s.status = "loading"; s.error = null; })
      .addCase(fetchUserOrders.fulfilled, (s, a) => { s.orders = a.payload; s.status = "idle"; })
      .addCase(fetchUserOrders.rejected, (s, a) => { s.status = "failed"; s.error = a.payload || "Couldn't load orders"; })
      .addCase(fetchHostOrders.fulfilled, (s, a) => { s.hostOrders = a.payload; })
      .addCase(fetchHostOrders.rejected, (s, a) => { s.error = a.payload || "Couldn't load orders"; })
      .addCase(fetchHostOrderStats.fulfilled, (s, a) => { s.hostCount = a.payload.total; s.hostDelivered = a.payload.delivered; s.hostRevenue = a.payload.revenue; })
      .addCase(fetchPendingOrders.fulfilled, (s, a) => { s.pendingOrders = a.payload; });
  },
});

export const { setOrders, setHostOrders, setCurrentOrder, setLoading, setOrdersError, clearOrders } = ordersSlice.actions;
export default ordersSlice.reducer;